import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readCheckoutSession,
  saveCheckoutSession,
  clearCheckoutSession,
  openCheckoutChannel,
} from '../utils/checkoutSessionUtil';
import {
  CHECKOUT_EVENTS,
  CHECKOUT_MESSAGE_SOURCES,
  CHECKOUT_SESSION_STATES,
  CHECKOUT_PAGE_PHASES,
  CHECKOUT_PREPARE_TIMEOUT_MS,
  CHECKOUT_PROBE_TIMEOUT_MS,
  CHECKOUT_CLOSE_DELAY_MS,
} from '../constants/bookingConst';

/**
 * Picks the starting phase from whatever session is stored.
 * @param {Object|null} session
 * @returns {string} a CHECKOUT_PAGE_PHASES value.
 */
const initialPhase = (session) => {
  if (session?.state === CHECKOUT_SESSION_STATES.READY && session.bookingData) return CHECKOUT_PAGE_PHASES.ACTIVE;
  if (session?.state === CHECKOUT_SESSION_STATES.PREPARING) return CHECKOUT_PAGE_PHASES.WAITING;
  return CHECKOUT_PAGE_PHASES.PROBING;
};

/**
 * Checkout-tab side of a checkout session, shared by every checkout page
 * (court booking and Open Play): loads the record the opening tab handed
 * over, keeps the message channel to it, and closes this tab when the
 * checkout is over.
 *
 * The record lives in `session.bookingData` whatever it is — a booking, or
 * an Open Play join — and `session.context` carries anything else the page
 * needs to render it.
 *
 * @param {string} sessionId - from the page URL.
 * @returns {{
 *   session: Object|null,
 *   phase: string,
 *   updateRecord: (record: Object) => void,
 *   post: (type: string, payload?: any) => void,
 *   endCheckout: (eventType: string|null, payload?: Object|null) => void,
 *   runOnce: (work: () => Promise<void>) => Promise<void>,
 * }}
 */
export const useCheckoutPageSession = (sessionId) => {
  const [session, setSession] = useState(() => readCheckoutSession(sessionId));
  const [phase, setPhase] = useState(() => initialPhase(session));
  const channelRef = useRef(null);
  // Set once the checkout has ended, so a late timer tick or message can't
  // run a second outcome.
  const endedRef = useRef(false);
  // Set while a release/discard is in flight — the payment countdown keeps
  // firing its expiry callback on every tick once the deadline has passed.
  const busyRef = useRef(false);

  /**
   * Ends the checkout: reports the outcome (when there is one to report),
   * forgets the session and closes this tab.
   * @param {string|null} eventType - CHECKOUT_EVENTS value to send, or null when the opening tab asked for the close.
   * @param {Object|null} [payload]
   */
  const endCheckout = useCallback((eventType, payload = null) => {
    if (endedRef.current) return;
    endedRef.current = true;
    if (eventType) channelRef.current?.post(eventType, payload);
    clearCheckoutSession(sessionId);
    setPhase(CHECKOUT_PAGE_PHASES.ENDED);
    // Short delay so the message above is delivered before the tab goes.
    // window.close() only works on a tab opened by script — which this one
    // was — so the ENDED notice is just the fallback.
    setTimeout(() => {
      try { window.opener?.focus(); } catch (error) { /* focus is best-effort */ }
      window.close();
    }, CHECKOUT_CLOSE_DELAY_MS);
  }, [sessionId]);

  // Channel to the opening tab.
  useEffect(() => {
    if (!sessionId) return undefined;
    const channel = openCheckoutChannel(sessionId, CHECKOUT_MESSAGE_SOURCES.CHECKOUT, (type, payload) => {
      if (type === CHECKOUT_EVENTS.SESSION && payload?.bookingData) {
        if (endedRef.current) return;
        setSession(payload);
        setPhase(CHECKOUT_PAGE_PHASES.ACTIVE);
      } else if (type === CHECKOUT_EVENTS.CLOSE) {
        // The opening tab took over (creation failed, or cancelled there).
        endCheckout(null);
      }
    });
    channelRef.current = channel;
    // Covers a record handed over before this tab finished loading, and
    // browsers where localStorage is unavailable.
    channel.post(CHECKOUT_EVENTS.REQUEST_SESSION);
    return () => channel.close();
  }, [sessionId, endCheckout]);

  // Give up waiting if the record never arrives.
  useEffect(() => {
    if (phase !== CHECKOUT_PAGE_PHASES.PROBING && phase !== CHECKOUT_PAGE_PHASES.WAITING) return undefined;
    const timeout = phase === CHECKOUT_PAGE_PHASES.PROBING ? CHECKOUT_PROBE_TIMEOUT_MS : CHECKOUT_PREPARE_TIMEOUT_MS;
    const timer = setTimeout(() => setPhase(CHECKOUT_PAGE_PHASES.INACTIVE), timeout);
    return () => clearTimeout(timer);
  }, [phase]);

  /**
   * Replaces the record in this tab's session and stores it, so a reload
   * shows the latest state (e.g. receipt already uploaded).
   * @param {Object} record
   */
  const updateRecord = useCallback((record) => {
    setSession((prev) => {
      const next = { ...prev, bookingData: record };
      saveCheckoutSession(sessionId, next);
      return next;
    });
  }, [sessionId]);

  /**
   * Sends a message to the opening tab.
   * @param {string} type - CHECKOUT_EVENTS value.
   * @param {any} [payload]
   */
  const post = useCallback((type, payload = null) => {
    channelRef.current?.post(type, payload);
  }, []);

  /**
   * Runs a release/discard once, showing the working spinner meanwhile.
   * Ignored while another one is running or after the checkout ended.
   * @param {() => Promise<void>} work
   * @returns {Promise<void>}
   */
  const runOnce = useCallback(async (work) => {
    if (busyRef.current || endedRef.current) return;
    busyRef.current = true;
    setPhase(CHECKOUT_PAGE_PHASES.WORKING);
    try {
      await work();
    } finally {
      busyRef.current = false;
      // Work that didn't end the checkout (it failed) hands the form back.
      if (!endedRef.current) setPhase(CHECKOUT_PAGE_PHASES.ACTIVE);
    }
  }, []);

  return { session, phase, updateRecord, post, endCheckout, runOnce };
};
